import {Injectable} from '@angular/core';
import {SharedFacadeService} from '@shared/shared-facade.service';
import {forkJoin, Observable} from 'rxjs';
import {StaffMember} from '@model/staffMember';
import {School} from '@model/school';
import {YearlyClassInformation} from '@model/yearly-class-information';
import {switchMap, take} from 'rxjs/operators';
import {SchoolService} from '@services/school.service';
import {select, Store} from '@ngrx/store';
import {getClassesWithClassLinks, StoreState} from '@redux/reducers';
import {
  educatorRemoved,
  educatorsAssigned,
  titularAdded,
  titularRemoved
} from '@redux/actions/yearly-classes-informations.action';
import {Holiday} from '@model/holiday';
import {
  HolidayRemovedAction,
  AgendaActivitySavedAction,
  AgendaActivityRemovedAction
} from '@redux/actions/school.action';
import {AgendaActivity} from '@model/agenda-activity';
import {Resource} from '@model/resource';
import {
  resourceDeleted,
  resourceSaved,
  schoolResourceYearlyReservationsLoaded,
  schoolResourceYearlyReservationDeleted,
  schoolResourceYearlyReservationSaved
} from '@redux/actions/resources.action';
import {Schedule} from '@model/schedule';
import {ResourceService} from '@services/resource.service';
import {SchoolResourceYearlyReservation} from '@model/school-resource-yearly-reservation';
import {ClassStaffMemberRelationsService} from '@services/class-staffmember-relations.service';
import {ClassStaffMemberRelation} from '@model/class-staffmember-relation';
import {getClassStaffMemberRelations} from '@redux/reducers/index';
import {ClassStaffMemberRelationsActions} from '@redux/actions/class-staffmember-relations.action copy';
import {ClassWithHorizonClassLink} from '@model/class';

@Injectable()
export class ManagementFacadeService {

  staffMembers$: Observable<StaffMember[]>;
  school$: Observable<School>;
  yearlyClassesInformationsEntities$: Observable<{ [classId: number]: YearlyClassInformation }>;
  isMobile$: Observable<boolean>;
  holidays$: Observable<Holiday[]>;
  agendaActivities$: Observable<AgendaActivity[]>;
  resources$: Observable<Resource[]>;
  schedule$: Observable<Schedule>;
  schoolResourceYearlyReservations$: Observable<SchoolResourceYearlyReservation[]>;
  classStaffMemberRelations$: Observable<ClassStaffMemberRelation[]>;
  classesWithHorizonClassLinks$: Observable<ClassWithHorizonClassLink[]>;
  activeSchoolYear$: Observable<number>;

  constructor(private _sharedFacade: SharedFacadeService,
              private _schoolService: SchoolService,
              private _classStaffMemberRelationsService: ClassStaffMemberRelationsService,
              private _resourceService: ResourceService,
              private _store: Store<StoreState>) {
    this.school$ = this._sharedFacade.school$;
    this.staffMembers$ = this._sharedFacade.staffMembers$;
    this.yearlyClassesInformationsEntities$ = this._sharedFacade.yearlyClassesInformationsEntities$;
    this.isMobile$ = this._sharedFacade.isMobile$;
    this.holidays$ = this._sharedFacade.holidays$;
    this.agendaActivities$ = this._sharedFacade.agendaActivities$;
    this.resources$ = this._sharedFacade.resources$;
    this.schedule$ = this._sharedFacade.schedule$;
    this.schoolResourceYearlyReservations$ = this._sharedFacade.schoolResourceYearlyReservations$;
    this.classStaffMemberRelations$ = this._store.select(getClassStaffMemberRelations); 
    this.classesWithHorizonClassLinks$ = this._store.pipe(select(getClassesWithClassLinks)) as Observable<ClassWithHorizonClassLink[]>;
    this.activeSchoolYear$ = this._sharedFacade.activeSchoolYear$;
}

  loadStaffMembers() {
    this._sharedFacade.loadStaffMembers();
  }

  loadYearlyClassesInformations() {
    this._sharedFacade.loadYearlyClassesInformations();
  }

  replaceTitular(classId: number, oldStaffmemberId: number, newStaffMemberId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.removeTitular(school.id, classId, oldStaffmemberId)),
    ).subscribe(() => {
      this._store.dispatch(titularRemoved({classId: classId, titularId: oldStaffmemberId}));
      this.school$.pipe(
        take(1),
        switchMap(school => this._schoolService.addTitular(school.id, classId, newStaffMemberId))
      ).subscribe(classTitulars => this._store.dispatch(
        titularAdded({
          classId: classId,
          titulars: classTitulars
        })));
    });
  }

  removeTitular(classId: number, oldStaffmemberId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.removeTitular(school.id, classId, oldStaffmemberId))
    ).subscribe(() => this._store.dispatch(titularRemoved({classId: classId, titularId: oldStaffmemberId})));
  }

  addTitular(classId: number, newStaffMemberId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.addTitular(school.id, classId, newStaffMemberId))
    ).subscribe(classTitulars => this._store.dispatch(
      titularAdded({
        classId: classId,
        titulars: classTitulars
      })));
  }

  replaceEducator(classId: number, oldStaffmemberId: number, newStaffMemberId: number, position: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.removeEducator(school.id, classId, oldStaffmemberId)),
    ).subscribe(() => this.assignEducator(classId, newStaffMemberId, position));
  }

  removeEducator(classId: number, staffMemberId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.removeEducator(school.id, classId, staffMemberId))
    ).subscribe(() => this._store.dispatch(educatorRemoved({classId: classId, educatorId: staffMemberId})));
  }

  assignEducator(classId: number, staffMemberId: number, position: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.assignEducator(school.id, classId, staffMemberId, position))
    ).subscribe(classEducator => this._store.dispatch(educatorsAssigned({classId: classId, educators: classEducator})));
  }

  loadHolidaysForCurrentSchoolYear() {
    this._sharedFacade.loadHolidaysForCurrentSchoolYear();
  }

  saveHolidays(holidays: Holiday[]) {
    this.school$.pipe(
      take(1),
      switchMap(school => forkJoin(holidays.map(holiday => this._schoolService.saveHoliday(school.id, holiday))))
    ).subscribe(() => this.loadHolidaysForCurrentSchoolYear());
  }

  removeHoliday(holidayId: string) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.deleteHoliday(school.id, holidayId))
    ).subscribe(() => this._store.dispatch(new HolidayRemovedAction(holidayId)));
  }

  loadAgendaActivities() {
    this._sharedFacade.loadAgendaActivities();
  }

  saveAgendaActivity(activity: AgendaActivity) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.saveAgendaActivity(school.id, activity))
    ).subscribe((id) => this._store.dispatch(new AgendaActivitySavedAction({id, ...activity})));
  }

  removeAgendaActivity(id: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._schoolService.removeAgendaActivity(school.id, id))
    ).subscribe(() => this._store.dispatch(new AgendaActivityRemovedAction(id)));
  }

  loadResources() {
    this._sharedFacade.loadResources();
  }

  saveResource(resource: Resource) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._resourceService.saveResource(school.id, resource))
    ).subscribe((_resource) => this._store.dispatch(resourceSaved({resource: _resource})));
  }

  removeResource(id: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._resourceService.removeResource(school.id, id))
    ).subscribe(() => this._store.dispatch(resourceDeleted({resourceId: id})));
  }

  loadSchoolResourceYearlyReservations(resourceId: number) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._resourceService.getSchoolResourceYearlyReservations(school.id, resourceId, school.activeSchoolYear))
    ).subscribe((schoolResourceYearlyReservations) =>
      this._store.dispatch(schoolResourceYearlyReservationsLoaded({schoolResourceYearlyReservations})));
  }

  saveSchoolResourceYearlyReservation(schoolResourceYearlyReservation: SchoolResourceYearlyReservation) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._resourceService.saveSchoolResourceYearlyReservation(school.id, schoolResourceYearlyReservation))
    ).subscribe(() => this._store.dispatch(schoolResourceYearlyReservationSaved({schoolResourceYearlyReservation})));
  }

  removeSchoolResourceYearlyReservation(schoolResourceYearlyReservation: SchoolResourceYearlyReservation) {
    this.school$.pipe(
      take(1),
      switchMap(school => this._resourceService.removeSchoolResourceYearlyReservation(school.id, schoolResourceYearlyReservation))
    ).subscribe(() => this._store.dispatch(schoolResourceYearlyReservationDeleted({ schoolResourceYearlyReservation })));
  }

  loadSchedule() {
    this._sharedFacade.loadSchedule();
  }

  loadClassStaffMemberRelations() {
    this._sharedFacade.loadClassStaffMemberRelations();
  }

  saveClassStaffMemberRelations(classIds: number[], staffMemberId: number) {
    if (!classIds.length) {
      return;
    }
    this.school$.pipe(
      take(1),
      switchMap(school => forkJoin(classIds.map(classId =>
        this._classStaffMemberRelationsService.postClassStaffMemberRelation(school.id, classId, staffMemberId, school.activeSchoolYear))))
    ).subscribe(classStaffMemberRelations => classStaffMemberRelations.forEach(classStaffMemberRelation =>
      this._store.dispatch(ClassStaffMemberRelationsActions.addClassStaffMemberRelation({ classStaffMemberRelation }))));
  }

  deleteClassStaffMemberRelations(relationIds: string[]) {
    if (!relationIds.length) {
      return;
    }
    this.school$.pipe(
      take(1),
      switchMap(school => forkJoin(relationIds.map(relationId =>
        this._classStaffMemberRelationsService.deleteClassStaffMemberRelation(school.id, relationId))))
    ).subscribe(() => relationIds.forEach(classStaffMemberRelationId =>
      this._store.dispatch(ClassStaffMemberRelationsActions.deleteClassStaffMemberRelation({ classStaffMemberRelationId }))));
  }

  loadHorizonClassLinks() {
    this._sharedFacade.loadHorizonClassLinks();
  }
}
